<script setup>
import { computed, ref } from "vue";
import { Link } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { resolveProfileTheme } from "@/Components/Profile/profileThemes.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import CountIcon from "@/Components/Profile/CountIcon.vue";
import FriendCardBadge from "@/Components/Profile/FriendCardBadge.vue";
import { useInertiaAction } from "@/composables/useInertiaAction.js";

/**
 * Person card for the redesigned Followers / Following grids (GH-851):
 * theme-gradient banner, avatar, counts bar, and Visit / Follow actions.
 */
const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    /**
     * Reload the page props after following / unfollowing (the Following
     * and Followers grids drop or add the card that way). Find Friends turns
     * this off: its results come from a POST search, so a GET reload would
     * land on the empty search screen (GH-1451) — the card flips itself
     * instead.
     */
    reload: {
        type: Boolean,
        default: true,
    },
});

const authUser = useAuthUser();

const isSelf = computed(() => authUser.value?.uuid === props.user.uuid);

// The banner renders the friend's profile theme flattened to the wide-short
// swatch angle, mirroring the theme picker previews.
const bannerGradient = computed(() =>
    resolveProfileTheme(props.user.profile_theme).gradient.replace(
        /linear-gradient\(\s*[\d.]+deg/,
        "linear-gradient(133deg",
    ),
);

const profileUrl = computed(() =>
    route("user.giftlists", { username: props.user.username }),
);

const avatarFailed = ref(false);

// With `reload` off the card owns its follow state after a toggle; with it
// on, the reloaded props win and these stay untouched.
const localFollowing = ref(null);
const localFollowersDelta = ref(0);
const isFollowing = computed(
    () => localFollowing.value ?? props.user.following,
);

const counts = computed(() => [
    { icon: "lists", value: props.user.list_count ?? 0, label: "Lists" },
    {
        icon: "followers",
        value: (props.user.followers_count ?? 0) + localFollowersDelta.value,
        label: "Followers",
    },
    {
        icon: "following",
        value: props.user.following_count ?? 0,
        label: "Following",
    },
]);

const countIconSizes = {
    lists: "h-[13px] w-[15px]",
    followers: "h-[14px] w-[19px]",
    following: "h-4 w-[18px]",
};

// Follow / unfollow. The endpoints return a bare boolean (not an Inertia
// response), so this goes through axios and reloads props afterwards —
// same pattern as the Explore bookmark toggle.
const { busy: followBusy, run: runFollow } = useInertiaAction();

async function toggleFollow() {
    if (followBusy.value) {
        return;
    }

    const wasFollowing = isFollowing.value;

    // Busy spans the request and the reload (GH-1159) so the button lands
    // directly in its new state.
    await runFollow(
        () =>
            wasFollowing
                ? axios.delete(
                      route("user.unfollow", { user: props.user.uuid }),
                  )
                : axios.post(route("user.follow", { user: props.user.uuid })),
        {
            reload: props.reload,
            onSuccess: () => {
                if (props.reload) {
                    return;
                }

                localFollowing.value = !wasFollowing;
                localFollowersDelta.value += wasFollowing ? -1 : 1;
            },
        },
    );
}
</script>

<template>
    <article
        class="flex flex-col rounded-[5px] border border-solid border-[#cdc4c2] bg-white p-4 [container-type:inline-size]"
    >
        <div class="relative">
            <div
                class="h-[73px] rounded-[4px]"
                :style="{ backgroundImage: bannerGradient }"
                data-test="friend-banner"
            ></div>
            <FriendCardBadge
                v-if="user.is_gift_hero"
                kind="gift-hero"
                class="absolute left-2 top-2"
            />
            <FriendCardBadge
                v-else-if="user.is_expert"
                kind="expert"
                class="absolute left-2 top-2"
            />
            <!-- The avatar leads to the profile like the name does (BH-56) -->
            <Link
                :href="profileUrl"
                class="absolute left-1/2 top-[25px] block size-[93px] -translate-x-1/2"
                :aria-label="`View ${user.presentation_name}'s profile`"
                data-test="friend-avatar-link"
            >
                <img
                    v-if="user.avatar?.profile_photo && !avatarFailed"
                    :src="user.avatar.profile_photo"
                    :alt="user.presentation_name"
                    class="size-full rounded-full border-[7px] border-solid border-white object-cover"
                    @error="avatarFailed = true"
                />
                <div
                    v-else
                    class="grid size-full place-items-center rounded-full border-[7px] border-solid border-white bg-brand-navy/80 font-brand-display text-[34px] font-black uppercase text-white"
                    aria-hidden="true"
                >
                    {{ user.presentation_name?.charAt(0) }}
                </div>
            </Link>
        </div>

        <Link
            :href="profileUrl"
            class="mt-12 text-center font-brand text-[22px] font-semibold leading-[1.2] text-[#102a3d] transition hover:text-brand-red"
        >
            {{ user.presentation_name }}
        </Link>

        <!-- Columns come from the card width (FriendCardGrid), not the
             viewport, so the pill sizes against the card itself: 13px per
             Figma from the 300px minimum card up, 11px in the one place a
             card runs narrower (beside the friends sidebar at 768–800px). -->
        <div
            class="mt-3.5 grid grid-cols-3 rounded-[2px] border border-solid border-[#e6dfdd] px-1.5 py-2.5 font-brand text-[13px] text-[#102a3d] [@container(max-width:267px)]:text-[11px]"
        >
            <span
                v-for="count in counts"
                :key="count.label"
                class="flex flex-col items-center gap-1"
            >
                <CountIcon
                    :icon="count.icon"
                    :class="countIconSizes[count.icon]"
                />
                <span class="whitespace-nowrap"
                    ><strong>{{ count.value }}</strong> {{ count.label }}</span
                >
            </span>
        </div>

        <!-- truncate clips overflow, so the line box needs room for
             descenders (BH-57) -->
        <p
            class="mt-5 truncate text-center font-brand text-[14px] leading-normal text-[#565a77]"
        >
            @{{ user.username }}
        </p>

        <!-- Standard primary CTAs (GH-999): same variant pairing and labels
             as the rail FollowButton — navy "Follow" at rest, white-on-red
             "Unfollow" once following. -->
        <div class="mt-auto flex gap-2.5 pt-5">
            <BrandButton :href="profileUrl" class="h-11 flex-1 text-[15px]">
                Visit
            </BrandButton>
            <BrandButton
                v-if="!isSelf"
                :variant="isFollowing ? 'whiteOnRed' : 'navy'"
                class="h-11 flex-1 text-[15px]"
                :class="isFollowing && 'ring-1 ring-brand-tan'"
                :loading="followBusy"
                @click="toggleFollow"
            >
                {{
                    followBusy
                        ? isFollowing
                            ? "Unfollowing"
                            : "Following"
                        : isFollowing
                          ? "Unfollow"
                          : "Follow"
                }}
            </BrandButton>
            <span
                v-else
                class="flex h-11 flex-1 items-center justify-center rounded-[5px] bg-brand-cream font-brand text-[15px] font-bold text-[#565a77]"
            >
                That’s Me!
            </span>
        </div>
    </article>
</template>
